import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchPendingCoaches,
  fetchApprovedCoaches,
  approveCoach,
  rejectCoach,
  deleteCoach,
} from '../../redux/slices/adminSlice';
import client from '../../apollo/client';
import { REQUEST_PASSWORD_RESET } from '../../apollo/mutations/user';
import extractErrorMessage from '../../utils/extractErrorMessage';
import logo from '../../assets/GymnazeLogoLightMode.png';
import Logout from '../../components/Logout/Logout';
import styles from './AdminPage.module.css';

const TABS = ['Pending Approval', 'Coaches', 'Tools'];

const AdminPage = () => {
  const dispatch = useDispatch();
  const { pendingCoaches, approvedCoaches, loading } = useSelector((state) => state.admin);

  const [activeTab, setActiveTab] = useState('Pending Approval');
  const [coachToDelete, setCoachToDelete] = useState(null);
  const [resetEmail, setResetEmail] = useState('');
  const [resetStatus, setResetStatus] = useState(null);

  useEffect(() => {
    dispatch(fetchPendingCoaches());
    dispatch(fetchApprovedCoaches());
  }, [dispatch]);

  const handleDeleteConfirm = () => {
    dispatch(deleteCoach(coachToDelete._id));
    setCoachToDelete(null);
  };

  const handleSendReset = async (e) => {
    e.preventDefault();
    setResetStatus(null);
    try {
      await client.mutate({
        mutation: REQUEST_PASSWORD_RESET,
        variables: { email: resetEmail },
      });
      setResetStatus({ type: 'success', message: 'Reset link sent.' });
      setResetEmail('');
    } catch (err) {
      setResetStatus({ type: 'error', message: extractErrorMessage(err) });
    }
  };

  return (
    <div className={styles.wrapper}>
      <div className={styles.header}>
        <img src={logo} alt="Gymnaze" className={styles.logo} />
        <Logout />
      </div>

      <div className={styles.layout}>
        <nav className={styles.sidebar}>
          {TABS.map((tab) => (
            <button
              key={tab}
              className={`${styles.tabBtn} ${activeTab === tab ? styles.tabBtnActive : ''}`}
              onClick={() => setActiveTab(tab)}
            >
              {tab}
              {tab === 'Pending Approval' && pendingCoaches.length > 0 && (
                <span className={styles.badge}>{pendingCoaches.length}</span>
              )}
            </button>
          ))}
        </nav>

        <main className={styles.content}>
          {activeTab === 'Pending Approval' && (
            <>
              <h2 className={styles.contentTitle}>Pending Approval</h2>
              {loading ? (
                <p className={styles.empty}>Loading...</p>
              ) : pendingCoaches.length === 0 ? (
                <p className={styles.empty}>No pending requests.</p>
              ) : (
                <div className={styles.tableWrapper}>
                  <table className={styles.table}>
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Email</th>
                        <th>Organization</th>
                        <th>Submitted</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pendingCoaches.map((coach) => (
                        <tr key={coach._id}>
                          <td>{coach.firstName} {coach.lastName}</td>
                          <td>{coach.email}</td>
                          <td>{coach.organization || '—'}</td>
                          <td>{new Date(parseInt(coach.createdAt)).toLocaleDateString()}</td>
                          <td className={styles.actions}>
                            <button
                              className={styles.btnApprove}
                              onClick={() => dispatch(approveCoach(coach._id))}
                            >
                              Approve
                            </button>
                            <button
                              className={styles.btnReject}
                              onClick={() => dispatch(rejectCoach(coach._id))}
                            >
                              Reject
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}

          {activeTab === 'Coaches' && (
            <>
              <h2 className={styles.contentTitle}>Coaches</h2>
              {approvedCoaches.length === 0 ? (
                <p className={styles.empty}>No coaches yet.</p>
              ) : (
                <div className={styles.tableWrapper}>
                  <table className={styles.table}>
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Email</th>
                        <th>Organization</th>
                        <th>Athletes</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {approvedCoaches.map((coach) => (
                        <tr key={coach._id}>
                          <td>{coach.firstName} {coach.lastName}</td>
                          <td>{coach.user?.email}</td>
                          <td>{coach.organization || '—'}</td>
                          <td>{coach.athletes?.length ?? 0}</td>
                          <td>
                            <button
                              className={styles.btnDelete}
                              onClick={() => setCoachToDelete(coach)}
                            >
                              Delete
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}

          {activeTab === 'Tools' && (
            <>
              <h2 className={styles.contentTitle}>Tools</h2>
              <div className={styles.toolSection}>
                <h3 className={styles.toolTitle}>Send Password Reset</h3>
                <p className={styles.toolDescription}>Send a password reset link to any registered user.</p>
                <form className={styles.toolForm} onSubmit={handleSendReset}>
                  <input
                    type="email"
                    className={styles.toolInput}
                    placeholder="Email address"
                    value={resetEmail}
                    onChange={(e) => setResetEmail(e.target.value)}
                    required
                  />
                  <button type="submit" className={styles.btnPrimary}>
                    Send Reset Link
                  </button>
                </form>
                {resetStatus && (
                  <p className={resetStatus.type === 'success' ? styles.statusSuccess : styles.statusError}>
                    {resetStatus.message}
                  </p>
                )}
              </div>
            </>
          )}
        </main>
      </div>

      {coachToDelete && (
        <div className={styles.modalOverlay}>
          <div className={styles.modal}>
            <h3 className={styles.modalTitle}>Delete Coach</h3>
            <p className={styles.modalBody}>
              Delete <strong>{coachToDelete.firstName} {coachToDelete.lastName}</strong>? This will
              remove their account, all athlete connections, and all associated data. This cannot be undone.
            </p>
            <div className={styles.modalActions}>
              <button className={styles.btnCancel} onClick={() => setCoachToDelete(null)}>
                Cancel
              </button>
              <button className={styles.btnDelete} onClick={handleDeleteConfirm}>
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPage;
